import React, { useState, useEffect, useContext } from 'react';
import { AuthContext, API } from '../App';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import Layout from '../components/Layout';
import { Users, Clock, AlertTriangle, FileText, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

const AdminDashboard = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [missingClockOuts, setMissingClockOuts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, usersRes, missingRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/users`),
        axios.get(`${API}/notifications/missing-clock-outs`)
      ]);
      
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setMissingClockOuts(missingRes.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="admin-dashboard">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-gray-900">Admin Dashboard</h1>
            <p className="text-gray-600 mt-2">Willkommen zurück, {user?.first_name}!</p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="card-hover border-l-4 border-l-blue-500" data-testid="total-users-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Mitarbeiter Gesamt</CardTitle>
              <Users className="w-5 h-5 text-blue-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900">{users.length}</div>
            </CardContent>
          </Card>

          <Card className="card-hover border-l-4 border-l-green-500" data-testid="hours-today-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Stunden Heute</CardTitle>
              <Clock className="w-5 h-5 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900">{stats?.total_hours_today || 0}h</div>
            </CardContent>
          </Card>

          <Card className="card-hover border-l-4 border-l-purple-500" data-testid="hours-month-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Stunden Monat</CardTitle>
              <TrendingUp className="w-5 h-5 text-purple-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900">{stats?.total_hours_month || 0}h</div>
            </CardContent>
          </Card>

          <Card className="card-hover border-l-4 border-l-red-500" data-testid="missing-clockouts-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Fehlende Ausstempelungen</CardTitle>
              <AlertTriangle className="w-5 h-5 text-red-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900">{missingClockOuts.length}</div>
            </CardContent>
          </Card>
        </div>

        {/* Missing Clock Outs Alert */}
        {missingClockOuts.length > 0 && (
          <Card className="border-l-4 border-l-red-500 bg-red-50" data-testid="missing-clockouts-alert">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-700">
                <AlertTriangle className="w-5 h-5" />
                Mitarbeiter mit fehlenden Ausstempelungen
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {missingClockOuts.map((entry) => (
                  <div key={entry.entry_id} className="flex items-center justify-between bg-white p-3 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900">{entry.user_name}</p>
                      <p className="text-sm text-gray-600">{entry.user_email}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-red-600 font-medium">{entry.hours_since_start.toFixed(1)}h aktiv</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick Actions */}
        <Card className="card-hover">
          <CardHeader>
            <CardTitle>Schnellzugriff</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Button
                onClick={() => navigate('/users')}
                className="h-20 text-base"
                variant="outline"
                data-testid="manage-users-button"
              >
                <Users className="w-5 h-5 mr-2" />
                Mitarbeiter verwalten
              </Button>
              <Button
                onClick={() => navigate('/reports')}
                className="h-20 text-base"
                variant="outline"
                data-testid="reports-button"
              >
                <FileText className="w-5 h-5 mr-2" />
                Berichte & Exporte
              </Button>
              <Button
                onClick={() => navigate('/projects')}
                className="h-20 text-base"
                variant="outline"
                data-testid="manage-projects-button"
              >
                <Clock className="w-5 h-5 mr-2" />
                Projekte verwalten
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* All Users List */}
        <Card className="card-hover">
          <CardHeader>
            <CardTitle>Alle Mitarbeiter</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {users.map((u) => (
                <div key={u.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100" data-testid={`user-item-${u.id}`}>
                  <div>
                    <p className="font-medium text-gray-900">{u.first_name} {u.last_name}</p>
                    <p className="text-sm text-gray-600">{u.email}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      u.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                    }`}>
                      {u.role === 'admin' ? 'Administrator' : 'Mitarbeiter'}
                    </span>
                    {u.is_active && (
                      <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                        Aktiv
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default AdminDashboard;
